import { useContext, useRef, useState, useEffect } from 'react';
import useEventSink from './useEventSink';
import { orchestrator } from 'lib_ui-core';
import { globalConfig, loadUseCase, metadata, constants } from 'lib_ui-services';
import { contexts, hooks } from 'lib_ui-primitives';
import useNavigation from './useNavigation';

const { useMemoizedObject } = hooks;

export const STATUS = {
    notStarted: 'not started',
    pending: 'pending',
    success: 'success',
    error: 'error'
};

const _p = {
    globalConfig,
    loadUseCase,
    setMetadata: metadata.setMetadata,
    orchestrateUseCase: orchestrator.orchestrateUseCase,
    useNavigation
};
export const _private = _p;

export default function useOrchestrator() {
    const orchestratorHasRun = useRef(false);
    const [status, setStatus] = useState(STATUS.notStarted);
    const [value, setValue] = useState(null);
    const [error, setError] = useState(null);

    const eventSink = useEventSink();
    const [subscribe] = eventSink;

    const session = useContext(contexts.SessionContext);
    if (!session) {
        throw new Error('useOrchestrator must be used within a SessionProvider');
    }
    if (session.tenant == null) {
        throw new Error('useOrchestrator must be used within a SessionProvider with a tenant');
    }
    if (session.useCase == null) {
        throw new Error('useOrchestrator must be used within a SessionProvider with a useCase');
    }
    if (session.profile == null) {
        throw new Error('useOrchestrator must be used within a SessionProvider with a profile');
    }

    _p.globalConfig().setGroupNumber(session.groupNumber ?? 0);
    _p.globalConfig().setFeatureFlags(session.allFeatureFlags ?? []);
    _p.useNavigation();

    // Try to avoid re-running this useEffect because of ref changes of equal objects.:
    const useCaseId = session.useCase['metaui:useCase']._id;
    const featureFlags = useMemoizedObject(session.allFeatureFlags ?? []);
    const role = useMemoizedObject(session.role ?? { title: 'USER', _id: constants.WELL_KNOWN_ROLE_IDS.USER });
    useEffect(() => {
        // Subscribe to metadata update events to refresh use case metadata when caches are invalidated
        // e.g. Caption Overrides, Tenant Settings, etc.
        const metadataUpdateEventContext = {
            verb: 'upsert',
            namespace: 'metaui',
            relation: 'useCaseVersion'
        };
        const unsubscribes = [
            subscribe(metadataUpdateEventContext, async () => {
                const payload = {
                    query: {
                        _id: useCaseId,
                        role,
                        featureFlags
                    }
                };
                // Reload the use case metadata when notified of server-side metadata updates
                const useCaseMetadata = await _p.loadUseCase(payload);
                _p.setMetadata(useCaseMetadata);
                // Setting the use case here should trigger a re-render of the UI on
                // the use case root which consumes this hook.
                setValue(useCaseMetadata);
            })
        ];
        return () => unsubscribes.map(u => u());
    }, [subscribe, useCaseId, featureFlags, role]);

    useEffect(() => {
        // Don't run the orchestrator more than once.  It will try to recreate the lokijs
        // in memory database, connect sockets again, register the service worker, etc.
        if (orchestratorHasRun.current) {
            return;
        }
        let allowStateUpdate = true;
        setStatus(STATUS.pending);
        setValue(null);
        setError(null);
        executeAsyncStuff();

        /**
         * Setup the use case and synchronize data if needed
         * @returns {Promise<void>}
         */
        async function executeAsyncStuff() {
            try {
                // Set this gate here because the eula seems to change the session
                // and cause this to run again before orchestrateUseCase is done.
                // If orchestration fails the UI will not recover anyway.
                orchestratorHasRun.current = true;
                const result = await _p.orchestrateUseCase(session);
                setValue(result.useCase);

                if (!__DISABLE_SYNC__) {
                    const purgedRelations = await result.synchronizeData();
                    if (purgedRelations.length && allowStateUpdate) {
                        // We need the UI to reload to make sure any purged relations are
                        // re-connected.  Set it to null, then back to the usecase to
                        // reload the UI.
                        setValue(null);
                        setValue(result.useCase);
                    }
                }
                if (allowStateUpdate) {
                    setStatus(STATUS.success);
                }
            } catch (error) {
                if (allowStateUpdate) {
                    setError(error);
                    setStatus(STATUS.error);
                }
            }
        }

        return () => {
            allowStateUpdate = false;
        };
    }, [session, eventSink]);

    return { status, error, useCase: value };
}
